import { useEffect, useState } from "react";
import axios from "axios";
import {
  Send,
  Map,
  Mail,
  Phone,
  Clock,
  AlertCircle,
  ChevronDown,
  MessageCircle,
} from "lucide-react";

import { FaFacebook, FaInstagram, FaTwitter, FaLinkedin, FaTiktok } from "react-icons/fa";

import HomeHeader2 from "@/components/homePage/HomeHeader2";

// Couleurs du drapeau du Niger (importées de votre code)
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState({
    isSubmitting: false,
    success: "",
    error: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const BackendUrl = process.env.REACT_APP_Backend_Url;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError("");
    setFormSuccess("");

    try {
      if (!formData.email || !formData.subject || !formData.message) {
        throw new Error("Veuillez remplir tous les champs obligatoires");
      }

      const payload = {
        name: formData.name?.trim(),
        email: formData.email?.trim().toLowerCase(),
        subject: formData.subject?.trim(),
        message: formData.message?.trim(),
      };

      const response = await axios.post(`${BackendUrl}/api/contact/send`, payload);

      setFormSuccess(
        response?.data?.message || "Votre message a ete envoye avec succes."
      );
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      setFormError(
        error?.response?.data?.message ||
          error.message ||
          "Impossible d'envoyer le message pour le moment."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Gestion de l'abonnement à la newsletter
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterStatus({
      isSubmitting: true,
      success: "",
      error: "",
    });

    try {
      const normalizedEmail = newsletterEmail.trim().toLowerCase();
      if (!normalizedEmail) {
        throw new Error("Veuillez saisir votre adresse email");
      }

      await axios.post(`${BackendUrl}/api/newsletter/subscribe`, {
        email: normalizedEmail,
        source: "contact-page",
      });

      setNewsletterStatus({
        isSubmitting: false,
        success:
          "Inscription enregistrée. Vérifiez votre e-mail pour confirmer l'abonnement.",
        error: "",
      });
      setNewsletterEmail("");
    } catch (error) {
      setNewsletterStatus({
        isSubmitting: false,
        success: "",
        error:
          error?.response?.data?.message ||
          error.message ||
          "Erreur lors de l'inscription à la newsletter.",
      });
    }
  };

  // Configuration des liens sociaux avec leurs URLs et icônes correspondantes
  // const socialLinks = [
  //   {
  //     name: "Facebook",
  //     url: "https://facebook.com/kassarmou",
  //     icon: <Facebook className="w-6 h-6" />,
  //   },
  //   {
  //     name: "Instagram",
  //     url: "https://instagram.com/kassarmou",
  //     icon: <Instagram className="w-6 h-6" />,
  //   },
  //   {
  //     name: "Twitter",
  //     url: "https://twitter.com/kassarmou",
  //     icon: <Twitter className="w-6 h-6" />,
  //   },
  //   {
  //     name: "LinkedIn",
  //     url: "https://linkedin.com/company/kassarmou",
  //     icon: <Linkedin className="w-6 h-6" />,
  //   },
  // ];

  const socialLinks = [
    {
        name: "tiktok",
        url: "https://www.tiktok.com/@amirseina?_t=ZN-8xhGdIjIl6I&_r=1",
        icon: <FaTiktok className="w-6 h-6" style={{ color: COLORS.green }} />,
      },
    {
      name: "Instagram",
      url: "https://instagram.com/kassarmou",
      icon: (
        <FaInstagram className="w-6 h-6 " style={{ color: COLORS.green }} />
      ),
    },
    // {
    //   name: "Twitter",
    //   url: "https://twitter.com/kassarmou",
    //   icon: <FaTwitter className="w-6 h-6 " style={{ color: COLORS.green }} />,
    // },
    // {
    //   name: "LinkedIn",
    //   url: "https://linkedin.com/company/kassarmou",
    //   icon: <FaLinkedin className="w-6 h-6 " style={{ color: COLORS.green }} />,
    // },
  ];

  return (
    <>
      <HomeHeader2 />

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div
          className="relative bg-gradient-to-r from-green-600 to-green-800"
          style={{ backgroundColor: COLORS.green }}
        >
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-y-0 right-0 w-1/2 bg-green-700 opacity-20 transform -skew-x-12"></div>
            <div className="absolute bottom-0 left-0 w-full h-24 bg-white transform -skew-y-3"></div>
          </div>
          <div className="relative container mx-auto px-4 py-16 md:py-24">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Contactez-nous
              </h1>
              <p className="text-lg md:text-xl text-white opacity-90 mb-8">
                Nous sommes là pour répondre à toutes vos questions concernant
                nos produits du Niger
              </p>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="container mx-auto px-4 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 transform transition hover:shadow-xl">
              <h2 className="text-2xl font-bold mb-6">
                Envoyez-nous un message
              </h2>

              {formSuccess && (
                <div className="mb-6 p-4 rounded-lg bg-green-50 border border-green-200 text-green-700">
                  {formSuccess}
                </div>
              )}

              {formError && (
                <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 flex items-start">
                  <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{formError}</span>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Votre nom
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition"
                      placeholder="..."
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Votre email *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition"
                      placeholder="..."
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label
                    htmlFor="subject"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Sujet *
                  </label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition"
                    placeholder="Question sur un produit"
                  />
                </div>

                <div className="mb-6">
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition"
                    placeholder="Votre message détaillé ici..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-6 py-3 text-white font-medium rounded-lg flex items-center justify-center transition-all focus:ring-4 focus:ring-orange-200"
                  style={{
                    backgroundColor: isSubmitting ? COLORS.gray : COLORS.orange,
                  }}
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Envoi en cours...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer
                    </span>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <div
                className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8 transform transition hover:shadow-xl border-l-4"
                style={{ borderLeftColor: COLORS.green }}
              >
                <h2 className="text-2xl font-bold mb-6">Nos informations</h2>

                <div className="space-y-6">
                  <div className="flex items-start">
                    <div
                      className="flex-shrink-0 p-2 rounded-full"
                      style={{ backgroundColor: COLORS.lightGreen }}
                    >
                      <Mail
                        className="w-6 h-6"
                        style={{ color: COLORS.green }}
                      />
                    </div>
                    <div className="ml-4">
                      <h3 className="font-medium text-gray-900">Email</h3>
                      <p className="text-gray-600 mt-1">
                        support@kassarmoumarket.com
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div
                      className="flex-shrink-0 p-2 rounded-full"
                      style={{ backgroundColor: COLORS.lightGreen }}
                    >
                      <MessageCircle
                        className="w-6 h-6"
                        style={{ color: COLORS.green }}
                      />
                    </div>
                    <div className="ml-4">
                      <h3 className="font-medium text-gray-900">WhatsApp</h3>
                      <div className="mt-2">
                        <a
                          href="https://wa.me/32465965436"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
                        >
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Nous contacter sur WhatsApp
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div
                      className="flex-shrink-0 p-2 rounded-full"
                      style={{ backgroundColor: COLORS.lightGreen }}
                    >
                      <Clock
                        className="w-6 h-6"
                        style={{ color: COLORS.green }}
                      />
                    </div>
                    <div className="ml-4">
                      <h3 className="font-medium text-gray-900">
                        Heures d'ouverture
                      </h3>
                      <p className="text-gray-600 mt-1">
                        Lun - Ven: 9h à 18h
                        <br />
                        Sam: 10h à 16h
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Media Links avec les icônes Lucide */}
              <div
                className="bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl shadow-lg p-6 md:p-8 transform transition hover:shadow-xl text-white"
                style={{ backgroundColor: COLORS.orange }}
              >
                <h2 className="text-2xl font-bold mb-6">Suivez-nous</h2>
                <p className="mb-6">
                  Restez connecté avec nous pour découvrir nos nouveaux produits
                  et promotions.
                </p>

                <div className="flex space-x-4">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Visitez notre page ${social.name}`}
                      className="p-3 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition"
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="bg-gray-100 py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-8 text-center">
                Notre emplacement
              </h2>
              {/* Placeholder for map - would be replaced with actual Google Map */}
              <div className="bg-white rounded-xl overflow-hidden shadow-lg aspect-video">
                <iframe
                  className="w-full h-full border-0"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d40299.37990509728!2d4.3317042!3d50.8364771!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c3c486740f9fff%3A0x10099ab2f4c8030!2sBruxelles%2C%20Belgique!5e0!3m2!1sfr!2s!4v1681665254954!5m2!1sfr!2s"
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Carte de notre emplacement à Bruxelles"
                ></iframe>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="container mx-auto px-4 py-12 md:py-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 text-center">
              Questions fréquentes
            </h2>

            <div className="space-y-4">
              {[
                {
                  question: "Comment suivre ma commande ?",
                  answer:
                    "Vous pouvez suivre votre commande en vous connectant à votre compte client et en accédant à la section 'Mes commandes'. Un numéro de suivi vous sera également envoyé par email dès que votre colis sera expédié.",
                },
                {
                  question: "Quels sont les délais de livraison ?",
                  answer:
                    "Les délais de livraison varient en fonction de votre localisation. Pour la Belgique, comptez 3-5 jours ouvrables. Pour les autres pays européens, comptez 5-10 jours ouvrables.",
                },
                {
                  question: "Puis-je modifier ou annuler ma commande ?",
                  answer:
                    "Vous pouvez modifier ou annuler votre commande dans les 24 heures suivant votre achat. Veuillez nous contacter rapidement par email ou téléphone pour toute modification.",
                },
                {
                  question: "Comment retourner un produit ?",
                  answer:
                    "Si vous n'êtes pas satisfait de votre achat, vous pouvez retourner le produit dans les 14 jours suivant sa réception. Contactez notre service client pour obtenir un numéro de retour et les instructions détaillées.",
                },
              ].map((faq, index) => (
                <details
                  key={index}
                  className="group bg-white rounded-lg shadow-md p-4 transition-all hover:shadow-lg border-l-4"
                  style={{
                    borderLeftColor:
                      index % 2 === 0 ? COLORS.green : COLORS.orange,
                  }}
                >
                  <summary className="flex justify-between items-center font-medium cursor-pointer list-none">
                    <span>{faq.question}</span>
                    <span className="transition group-open:rotate-180">
                      <ChevronDown className="w-5 h-5" />
                    </span>
                  </summary>
                  <p
                    className="text-gray-600 mt-4 pl-4 border-l-2"
                    style={{ borderLeftColor: COLORS.lightGray }}
                  >
                    {faq.answer}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </div>

        {/* Newsletter Section améliorée avec gestion d'état */}
        <div
          className="relative py-12 overflow-hidden"
          style={{ backgroundColor: COLORS.paleGreen }}
        >
          <div className="absolute top-0 right-0 -translate-y-1/2">
            <div
              className="w-64 h-64 rounded-full opacity-10"
              style={{ backgroundColor: COLORS.green }}
            ></div>
          </div>
          <div className="absolute bottom-0 left-0 translate-y-1/2">
            <div
              className="w-80 h-80 rounded-full opacity-10"
              style={{ backgroundColor: COLORS.orange }}
            ></div>
          </div>
          <div className="container mx-auto px-4 relative">
            <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-lg p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Restez informé</h2>
              <p className="text-gray-600 mb-6">
                Abonnez-vous à notre newsletter pour recevoir les dernières
                nouvelles, promotions et offres exclusives.
              </p>

              {/* Messages d'état pour la newsletter */}
              {newsletterStatus.success && (
                <div className="mb-6 p-4 rounded-lg bg-green-50 border border-green-200 text-green-700">
                  {newsletterStatus.success}
                </div>
              )}

              {newsletterStatus.error && (
                <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 flex items-start">
                  <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{newsletterStatus.error}</span>
                </div>
              )}

              <form
                onSubmit={handleNewsletterSubmit}
                className="flex flex-col md:flex-row gap-3"
              >
                <input
                  type="email"
                  placeholder="Votre adresse email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  className="flex-grow px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition"
                  required
                />
                <button
                  type="submit"
                  disabled={newsletterStatus.isSubmitting}
                  className="px-6 py-3 text-white font-medium rounded-lg transition-all focus:ring-4 focus:ring-green-200 whitespace-nowrap flex items-center justify-center"
                  style={{
                    backgroundColor: newsletterStatus.isSubmitting
                      ? COLORS.gray
                      : COLORS.green,
                  }}
                >
                  {newsletterStatus.isSubmitting ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Traitement...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <Send className="w-4 h-4 mr-2" />
                      S'abonner
                    </span>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
